import api from '../../../services/api';
import { THEME_PREFERENCES_VERSION } from './constants';
import { DEFAULT_THEME_PREFERENCES, type ThemeMode, type ThemePreferences } from './types';

interface ThemePreferencesApiResponse {
  themeMode?: unknown;
  theme_mode?: unknown;
  version?: unknown;
  updatedAt?: unknown;
  updated_at?: unknown;
  preferences?: unknown;
}

const isThemeMode = (value: unknown): value is ThemeMode => {
  return value === 'light' || value === 'dark' || value === 'auto';
};

const normalize = (payload: ThemePreferencesApiResponse | null | undefined): ThemePreferences => {
  if (!payload) {
    return DEFAULT_THEME_PREFERENCES;
  }

  const source =
    payload.preferences && typeof payload.preferences === 'object'
      ? (payload.preferences as ThemePreferencesApiResponse)
      : payload;

  const themeModeCandidate =
    source.themeMode ?? source.theme_mode ?? DEFAULT_THEME_PREFERENCES.themeMode;

  return {
    themeMode: isThemeMode(themeModeCandidate) ? themeModeCandidate : DEFAULT_THEME_PREFERENCES.themeMode,
    version: typeof source.version === 'number' ? source.version : THEME_PREFERENCES_VERSION,
    updatedAt:
      typeof source.updatedAt === 'string'
        ? source.updatedAt
        : typeof source.updated_at === 'string'
          ? source.updated_at
          : undefined,
  };
};

export const themePreferencesService = {
  async get(): Promise<ThemePreferences> {
    const response = await api.get<ThemePreferencesApiResponse>('/users/me/preferences');
    return normalize(response.data);
  },

  async update(preferences: ThemePreferences): Promise<ThemePreferences> {
    const payload = {
      themeMode: preferences.themeMode,
      version: preferences.version || THEME_PREFERENCES_VERSION,
    };
    const response = await api.put<ThemePreferencesApiResponse>('/users/me/preferences', payload);
    if (!response.data || (typeof response.data === 'object' && Object.keys(response.data).length === 0)) {
      return {
        ...payload,
        updatedAt: new Date().toISOString(),
      };
    }
    return normalize(response.data);
  },
};
